/**
 * Job Scout query hooks.
 *
 * Review and promotion both invalidate the posting list AND the run list: a run
 * row shows its posting count, and promoting changes what is left to act on, so
 * leaving either stale would show the operator work that is already done.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';

import { scoutApi, type ScoutPagination } from '@/lib/api/scout.api';
import type {
  PromoteScoutPostingsRequest,
  PromoteScoutPostingsResponse,
  ReviewScoutPostingsRequest,
  ReviewScoutPostingsResponse,
  ScoutPostingDetail,
  ScoutPostingFilters,
  ScoutPostingsResponse,
  ScoutProgram,
  ScoutRunFilters,
  ScoutRunSummary,
  ScoutRunsResponse,
  ScoutRunTasksResponse,
  ScoutTaskStatus,
  ScoutTarget,
  ScoutTargetFilters,
  UpdateScoutProgramRequest,
  UpdateScoutTargetRequest,
} from '@repo/types';

// ==================== Query Keys ====================

export const scoutKeys = {
  all: ['scout'] as const,
  programs: () => [...scoutKeys.all, 'programs'] as const,
  targets: (filters?: ScoutTargetFilters) => [...scoutKeys.all, 'targets', filters] as const,
  runs: (filters?: ScoutRunFilters, pagination?: ScoutPagination) =>
    [...scoutKeys.all, 'runs', filters, pagination] as const,
  run: (id: string) => [...scoutKeys.all, 'run', id] as const,
  runTasks: (id: string, status?: ScoutTaskStatus) =>
    [...scoutKeys.all, 'run', id, 'tasks', status ?? 'all'] as const,
  postings: (filters?: ScoutPostingFilters, pagination?: ScoutPagination) =>
    [...scoutKeys.all, 'postings', filters, pagination] as const,
  posting: (id: string) => [...scoutKeys.all, 'posting', id] as const,
};

// ==================== Queries ====================

export function useScoutPrograms(
  options?: Partial<UseQueryOptions<ScoutProgram[], Error>>
) {
  return useQuery<ScoutProgram[], Error>({
    queryKey: scoutKeys.programs(),
    queryFn: () => scoutApi.getPrograms(),
    ...options,
  });
}

export function useScoutTargets(
  filters?: ScoutTargetFilters,
  options?: Partial<UseQueryOptions<ScoutTarget[], Error>>
) {
  return useQuery<ScoutTarget[], Error>({
    queryKey: scoutKeys.targets(filters),
    queryFn: () => scoutApi.getTargets(filters),
    ...options,
  });
}

export function useScoutRuns(
  filters?: ScoutRunFilters,
  pagination?: ScoutPagination,
  options?: Partial<UseQueryOptions<ScoutRunsResponse, Error>>
) {
  return useQuery<ScoutRunsResponse, Error>({
    queryKey: scoutKeys.runs(filters, pagination),
    queryFn: () => scoutApi.getRuns(filters, pagination),
    ...options,
  });
}

export function useScoutRun(
  id: string,
  options?: Partial<UseQueryOptions<ScoutRunSummary, Error>>
) {
  return useQuery<ScoutRunSummary, Error>({
    queryKey: scoutKeys.run(id),
    queryFn: () => scoutApi.getRun(id),
    enabled: Boolean(id),
    ...options,
  });
}

/**
 * The sites in one run. Used by the run sheet to answer "which ones failed" —
 * the run list can only say how many did.
 */
export function useScoutRunTasks(
  id: string,
  status?: ScoutTaskStatus,
  options?: Partial<UseQueryOptions<ScoutRunTasksResponse, Error>>
) {
  return useQuery<ScoutRunTasksResponse, Error>({
    queryKey: scoutKeys.runTasks(id, status),
    queryFn: () => scoutApi.getRunTasks(id, status),
    enabled: Boolean(id),
    ...options,
  });
}

export function useScoutPostings(
  filters?: ScoutPostingFilters,
  pagination?: ScoutPagination,
  options?: Partial<UseQueryOptions<ScoutPostingsResponse, Error>>
) {
  return useQuery<ScoutPostingsResponse, Error>({
    queryKey: scoutKeys.postings(filters, pagination),
    queryFn: () => scoutApi.getPostings(filters, pagination),
    ...options,
  });
}

/** Full detail for one posting — fetched only when the review sheet opens. */
export function useScoutPosting(
  id: string | null,
  options?: Partial<UseQueryOptions<ScoutPostingDetail, Error>>
) {
  return useQuery<ScoutPostingDetail, Error>({
    queryKey: scoutKeys.posting(id ?? ''),
    queryFn: () => scoutApi.getPosting(id as string),
    enabled: Boolean(id),
    ...options,
  });
}

// ==================== Mutations ====================

export function useReviewScoutPostings() {
  const queryClient = useQueryClient();

  return useMutation<ReviewScoutPostingsResponse, Error, ReviewScoutPostingsRequest>({
    mutationFn: (payload) => scoutApi.reviewPostings(payload),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      const verb = variables.status === 'approved' ? 'Approved' : 'Rejected';
      toast.success(`${verb} ${result.updated} posting${result.updated === 1 ? '' : 's'}`);
    },
    onError: (error) => toast.error(error.message || 'Could not update those postings'),
  });
}

export function useUpdateScoutProgram() {
  const queryClient = useQueryClient();

  return useMutation<ScoutProgram, Error, { id: string } & UpdateScoutProgramRequest>({
    mutationFn: ({ id, ...payload }) => scoutApi.updateProgram(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      toast.success('Program updated');
    },
    onError: (error) => toast.error(error.message || 'Could not update that program'),
  });
}

/**
 * Rename a target.
 *
 * Invalidates every scout query, not just the target list: the name is shown
 * against postings and run tasks too, and leaving those stale would show two
 * different names for the same site on adjacent pages.
 */
export function useUpdateScoutTarget() {
  const queryClient = useQueryClient();

  return useMutation<ScoutTarget, Error, { id: string } & UpdateScoutTargetRequest>({
    mutationFn: ({ id, ...payload }) => scoutApi.updateTarget(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      toast.success('Target updated');
    },
    onError: (error) => toast.error(error.message || 'Could not update that target'),
  });
}

/**
 * Hide a run from the list, or bring it back. Reversible and destroys nothing,
 * so it needs no confirmation — the toast offers the way back instead.
 */
export function useArchiveScoutRun() {
  const queryClient = useQueryClient();

  return useMutation<ScoutRunSummary, Error, { id: string; archived: boolean }>({
    mutationFn: ({ id, archived }) => scoutApi.archiveRun(id, archived),
    onSuccess: (_run, { archived }) => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      toast.success(archived ? 'Run archived' : 'Run restored');
    },
    onError: (error) => toast.error(error.message || 'Could not archive that run'),
  });
}

/**
 * Delete a run and everything it produced.
 *
 * Invalidates every scout query, not just the run list: the run's postings go
 * with it, so the review queue and its run filter are both stale. The server
 * refuses a running run, and refuses one holding promoted postings — those
 * errors are surfaced verbatim because they tell the operator what to do
 * instead ("Archive the run instead").
 */
export function useDeleteScoutRun() {
  const queryClient = useQueryClient();

  return useMutation<{ postings: number; tasks: number }, Error, string>({
    mutationFn: (id) => scoutApi.deleteRun(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      toast.success(
        `Run deleted — ${result.postings} posting${result.postings === 1 ? '' : 's'} removed`
      );
    },
    onError: (error) => toast.error(error.message || 'Could not delete that run'),
  });
}

export function usePromoteScoutPostings() {
  const queryClient = useQueryClient();

  return useMutation<PromoteScoutPostingsResponse, Error, PromoteScoutPostingsRequest>({
    mutationFn: (payload) => scoutApi.promotePostings(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: scoutKeys.all });
      // Also stale now: promotion writes rows into the fetcher's queue, which
      // the Scraping Queue page reads.
      queryClient.invalidateQueries({ queryKey: ['scraping'] });

      if (result.promoted > 0) {
        toast.success(
          `Sent ${result.promoted} posting${result.promoted === 1 ? '' : 's'} to the scraping queue`
        );
      } else if (result.oversizedUrl > 0) {
        toast.warning('Those postings have URLs too long for the scraping queue');
      } else if (result.alreadyPromoted > 0) {
        toast.info('Those postings were already sent to the scraping queue');
      } else if (result.notApproved > 0) {
        toast.warning('Approve postings before promoting them');
      } else {
        toast.info('Nothing to promote');
      }
    },
    onError: (error) => toast.error(error.message || 'Could not promote those postings'),
  });
}
